// components/ui/DropdownMenu.tsx
// UI Phase 1 primitive: menu on Radix DropdownMenu. Keyboard navigation
// (arrows/Home/End/typeahead), aria menu semantics, and outside-click come
// from Radix. Successor (over Phase 2) to the hand-rolled portal dropdowns.

import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface DropdownMenuItemSpec {
  key: string;
  label: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export interface DropdownMenuProps {
  /** The trigger element (rendered via asChild — must accept a ref, e.g. <Button>) */
  trigger: ReactNode;
  items: DropdownMenuItemSpec[];
  align?: 'start' | 'end';
}

const DropdownMenu = ({ trigger, items, align = 'end' }: DropdownMenuProps) => (
  <RadixDropdown.Root>
    <RadixDropdown.Trigger asChild>{trigger}</RadixDropdown.Trigger>
    <RadixDropdown.Portal>
      <RadixDropdown.Content
        align={align}
        sideOffset={6}
        className={cn(
          'z-50 min-w-[180px] bg-surface-card border border-line-soft rounded-card shadow-lg p-1',
          'animate-scale-in'
        )}
      >
        {items.map((item) => (
          <RadixDropdown.Item
            key={item.key}
            disabled={item.disabled}
            onSelect={item.onSelect}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm rounded-control cursor-pointer select-none',
              'focus:outline-none data-[highlighted]:bg-surface-sunken',
              'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
              item.danger ? 'text-status-critical' : 'text-ink'
            )}
          >
            {item.label}
          </RadixDropdown.Item>
        ))}
      </RadixDropdown.Content>
    </RadixDropdown.Portal>
  </RadixDropdown.Root>
);

export default DropdownMenu;
